import { useState, useCallback } from 'react';

/**
 * Custom hook to manage all modal states in the Universal Quoter
 * Centralizes modal open/close logic to reduce component complexity
 */
const useModalManager = () => {
  // Modal visibility states
  const [openModal, setOpenModal] = useState(null);
  const [isQuoteSetupOverlayOpen, setIsQuoteSetupOverlayOpen] = useState(false);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isFirelightModalOpen, setIsFirelightModalOpen] = useState(false);
  const [isQuoteActionModalOpen, setIsQuoteActionModalOpen] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [isShareModelOpen, setIsShareModelOpen] = useState(false);

  // Modal data states
  const [compareModalData, setCompareModalData] = useState(null);
  const [detailsModalData, setDetailsModalData] = useState(null);
  const [firelightModalData, setFirelightModalData] = useState(null);
  const [quoteActionModalData, setQuoteActionModalData] = useState(null);
  const [shareModelData, setShareModelData] = useState(null);
  const [overlayFormData, setOverlayFormData] = useState(null);

  // Follow-up modal states (for eTickets, eApps, etc.)
  const [followUpModalType, setFollowUpModalType] = useState(null);
  const [followUpModalData, setFollowUpModalData] = useState(null);

  // E-ticket/E-app states
  const [psgEapp, setPsgEapp] = useState(null);
  const [anicoEticketGuid, setAnicoEticketGuid] = useState(null);
  const [paclifeEticketGuid, setPaclifeEticketGuid] = useState(null);
  const [protectiveEticketGuid, setProtectiveEticketGuid] = useState(null);
  const [sbliEticketGuid, setSbliEticketGuid] = useState(null);
  const [nmbEticketGuid, setNmbEticketGuid] = useState(null);
  const [firelightEticket, setFirelightEticket] = useState(null);
  const [firelightEapp, setFirelightEapp] = useState(null);

  // Additional UI states
  const [hideQuoteCardDetails, setHideQuoteCardDetails] = useState(false);

  /**
   * Opens a specific modal by name
   */
  const openModalByName = useCallback((modalName, data = null) => {
    switch (modalName) {
      case 'carriers':
      case 'health-analyzer':
      case 'features':
        setOpenModal(modalName);
        break;
      case 'quote-setup':
        setIsQuoteSetupOverlayOpen(true);
        if (data) setOverlayFormData(data);
        break;
      case 'compare':
        setIsCompareModalOpen(true);
        if (data) setCompareModalData(data);
        break;
      case 'details':
        setIsDetailsModalOpen(true);
        if (data) setDetailsModalData(data);
        break;
      case 'firelight':
        setIsFirelightModalOpen(true);
        if (data) setFirelightModalData(data);
        break;
      case 'quote-action':
        setIsQuoteActionModalOpen(true);
        if (data) setQuoteActionModalData(data);
        break;
      case 'pdf':
        setShowPdfModal(true);
        break;
      case 'share':
        setIsShareModelOpen(true);
        if (data) setShareModelData(data);
        break;
      default:
        console.warn(`Unknown modal: ${modalName}`);
    }
  }, []);

  /**
   * Closes a specific modal by name
   */
  const closeModalByName = useCallback(modalName => {
    switch (modalName) {
      case 'carriers':
      case 'health-analyzer':
      case 'features':
        setOpenModal(null);
        break;
      case 'quote-setup':
        setIsQuoteSetupOverlayOpen(false);
        setOverlayFormData(null);
        break;
      case 'compare':
        setIsCompareModalOpen(false);
        setCompareModalData(null);
        break;
      case 'details':
        setIsDetailsModalOpen(false);
        setDetailsModalData(null);
        break;
      case 'firelight':
        setIsFirelightModalOpen(false);
        setFirelightModalData(null);
        break;
      case 'quote-action':
        setIsQuoteActionModalOpen(null);
        setQuoteActionModalData(null);
        break;
      case 'pdf':
        setShowPdfModal(false);
        break;
      case 'share':
        setIsShareModelOpen(false);
        setShareModelData(null);
        break;
      case 'follow-up':
        setFollowUpModalType(null);
        setFollowUpModalData(null);
        break;
      default:
        console.warn(`Unknown modal: ${modalName}`);
    }
  }, []);

  /**
   * Closes all modals
   */
  const closeAllModals = useCallback(() => {
    setOpenModal(null);
    setIsQuoteSetupOverlayOpen(false);
    setIsCompareModalOpen(false);
    setIsDetailsModalOpen(false);
    setIsFirelightModalOpen(false);
    setIsQuoteActionModalOpen(null);
    setShowPdfModal(false);
    setIsShareModelOpen(false);
    setFollowUpModalType(null);
    setFollowUpModalData(null);
    setCompareModalData(null);
    setDetailsModalData(null);
    setFirelightModalData(null);
    setQuoteActionModalData(null);
    setShareModelData(null);
    setOverlayFormData(null);
  }, []);

  /**
   * Opens a follow-up modal (e-ticket/e-app)
   */
  const openFollowUpModal = useCallback((type, data) => {
    setFollowUpModalType(type);
    setFollowUpModalData(data);
  }, []);

  return {
    // Modal visibility states
    openModal,
    isQuoteSetupOverlayOpen,
    isCompareModalOpen,
    isDetailsModalOpen,
    isFirelightModalOpen,
    isQuoteActionModalOpen,
    showPdfModal,
    isShareModelOpen,
    followUpModalType,

    // Modal data
    compareModalData,
    detailsModalData,
    firelightModalData,
    quoteActionModalData,
    shareModelData,
    overlayFormData,
    followUpModalData,

    // E-ticket/E-app states
    psgEapp,
    anicoEticketGuid,
    paclifeEticketGuid,
    protectiveEticketGuid,
    sbliEticketGuid,
    nmbEticketGuid,
    firelightEticket,
    firelightEapp,

    // Additional UI states
    hideQuoteCardDetails,

    // Setters for direct access when needed
    setOpenModal,
    setIsQuoteSetupOverlayOpen,
    setIsCompareModalOpen,
    setIsDetailsModalOpen,
    setIsFirelightModalOpen,
    setIsQuoteActionModalOpen,
    setShowPdfModal,
    setIsShareModelOpen,
    setCompareModalData,
    setDetailsModalData,
    setFirelightModalData,
    setQuoteActionModalData,
    setShareModelData,
    setOverlayFormData,
    setFollowUpModalType,
    setFollowUpModalData,
    setPsgEapp,
    setAnicoEticketGuid,
    setPaclifeEticketGuid,
    setProtectiveEticketGuid,
    setSbliEticketGuid,
    setNmbEticketGuid,
    setFirelightEticket,
    setFirelightEapp,
    setHideQuoteCardDetails,

    // Actions
    openModalByName,
    closeModalByName,
    closeAllModals,
    openFollowUpModal
  };
};

export default useModalManager;
